import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// Components & Pages
import Home from './pages/Home';
import Sidebar from './pages/Sidebar';
import Registration from './pages/Registration';
import ForgotPassword from './pages/ForgotPassword';
import AdminLogin from './pages/Admin/AdminLogin';
import RegControl from './pages/Admin/RegControl';

import Overview from './components/OverView/Overview';
import InfoManage from "./components/InfoManage/InfoManage";
import ManageDevice from './components/DeviceManage/ManageDevice';
import DiagnosticTools from "./components/DiagnosticTools/DiagnosticTools";
import LogManage from './components/LogManage/LogManage';
import TrafficMonitor from './components/TrafficMonitor/TrafficMonitor';
import OLTManage from "./components/OLTManage/OLTManage";
import KeyManage from "./components/KeyManage/KeyManage";
import LiveTV from "./components/LiveTV/LiveTV";

import './App.css';

const App = () => {
    const [loggedIn, setLoggedIn] = useState(() => localStorage.getItem('loggedIn') === 'true');
    const { i18n } = useTranslation();
    const [language, setLanguage] = useState(i18n.language);

    // --- Theme State & Management ---
    const [theme, setTheme] = useState(() => localStorage.getItem('n_tools_theme') || 'dark');

    useEffect(() => {
        localStorage.setItem('n_tools_theme', theme);
        document.documentElement.setAttribute('data-theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
    };

    // --- Global Protection Logic ---
    useEffect(() => {
        const interceptor = axios.interceptors.request.use((config) => {
            const userRole = localStorage.getItem('userRole');
            const restrictedMethods = ['post', 'put', 'delete', 'patch'];
            
            if (userRole === 'viewer' && restrictedMethods.includes(config.method.toLowerCase())) {
                alert("🔒 Read-Only Mode: You don't have permission to modify data.");
                return Promise.reject(new Error("Viewer blocked"));
            }
            return config;
        });

        const handleGlobalClick = (e) => {
            const userRole = localStorage.getItem('userRole');
            if (userRole === 'viewer') {
                const target = e.target.closest('button, a.btn, input[type="submit"]');
                if (target) {
                    const btnText = target.innerText?.toLowerCase() || "";
                    const isForbidden = 
                        btnText.includes("save") || 
                        btnText.includes("update") || 
                        btnText.includes("delete") || 
                        btnText.includes("add") || 
                        btnText.includes("edit") ||
                        btnText.includes("submit");

                    if (isForbidden) {
                        e.preventDefault();
                        e.stopPropagation();
                        alert("🚫 Access Restricted: Viewers can only view records, not change them.");
                    }
                }
            }
        };

        document.addEventListener('click', handleGlobalClick, true);

        return () => {
            axios.interceptors.request.eject(interceptor);
            document.removeEventListener('click', handleGlobalClick, true);
        };
    }, []);

    const handleLanguageChange = (event) => {
        const newLanguage = event.target.value;
        setLanguage(newLanguage);
        i18n.changeLanguage(newLanguage);
    };

    const handleLogout = () => {
        localStorage.clear();
        setLoggedIn(false);
    };

    // ------------------ Logged-Out Routes ------------------
    if (!loggedIn) {
        return (
            <Routes>
                <Route 
                    path="/" 
                    element={
                        <Home 
                            setLoggedIn={setLoggedIn} 
                            language={language} 
                            handleLanguageChange={handleLanguageChange} 
                        />
                    } 
                />
                <Route 
                    path="/home" 
                    element={
                        <Home 
                            setLoggedIn={setLoggedIn} 
                            language={language} 
                            handleLanguageChange={handleLanguageChange} 
                        />
                    } 
                />
                <Route path="/register" element={<Registration />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/administrator-login" element={<AdminLogin setLoggedIn={setLoggedIn} />} />
                <Route path="*" element={<Navigate to="/home" />} />
            </Routes>
        );
    }

    // ------------------ Logged-In Dashboard ------------------
    return (
        <div className="dashboard-container flex">
            <Sidebar onLogout={handleLogout} />

            <div className="main-content flex-1">
                {/* Clean Top Bar with Minimal Theme Icon Button */}
                <div className="top-header-bar">
                    <button onClick={toggleTheme} className="theme-icon-btn" title="Toggle Theme">
                        {theme === 'dark' ? '☀️' : '🌙'}
                    </button>
                </div>

                <Routes>
                    <Route path="/" element={<Overview />} />
                    <Route path="/reg-control" element={<RegControl />} />
                    <Route path="/info-manage/*" element={<InfoManage />} />
                    <Route path="/device-manage/*" element={<ManageDevice />} />
                    <Route path="/diagnostic-tools/*" element={<DiagnosticTools />} />
                    <Route path="/log-manage/*" element={<LogManage />} />
                    <Route path="/traffic-monitor/*" element={<TrafficMonitor />} />
                    <Route path="/olt-manage/*" element={<OLTManage />} />
                    <Route path="/key-manage/*" element={<KeyManage />} />
                    <Route path="/livetv/*" element={<LiveTV />} />
                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </div>
        </div>
    );
};

export default App;